import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, CalendarClock, ChevronDown, CloudSun, Eye, EyeOff, Tv, X, Youtube, Fuel } from 'lucide-react';
import api from '../api/client';
import ChampionshipRegistrationModal from '../components/ChampionshipRegistrationModal';
import RacingTyreIcon from '../components/RacingTyreIcon';
import PracticeTab from '../components/PracticeTab';
import RaceResultsViewer from '../components/RaceResultsViewer';
import { Championship, ChampionshipRegistration, ChampionshipRound, ResultSessionSummary, SimgridSignout } from '../types';
import { SortDirection, getSortIndicator, sortRows } from '../utils/sort';
import { formatDateTime, parseRoundDateTime } from '../utils/time';
import './ChampionshipDetailsPage.css';

interface DriverStandingsRow {
  driverId: string;
  driverName: string;
  carNumber: string;
  carModel: string;
  roundResults: Array<{ hasResult: boolean; position: number | null; points: number }>;
  droppedIndices: Set<number>;
  penaltyPoints: number;
  totalPoints: number;
}

interface RegistrationSelection {
  eventId: string;
  carName: string;
}

interface ChampionshipDetailsPageProps {
  championship: Championship;
  onBack: () => void;
  activeTab: 'info' | 'standings' | 'races' | 'drivers' | 'practice';
  onTabChange: (tab: 'info' | 'standings' | 'races' | 'drivers' | 'practice') => void;
  broadcastPlatform?: 'youtube' | 'twitch' | '';
  broadcastChannel?: string;
  defaultRulebookType?: 'text' | 'link' | 'pdf';
  defaultRulebook?: string;
  defaultRulebookPdfDataUrl?: string | null;
  displayTimezone?: string;
}

const buildBroadcastUrl = (platform: string, channel: string): string => {
  if (!channel) return '';
  if (platform === 'youtube') {
    if (channel.startsWith('http')) return channel;
    const handle = channel.startsWith('@') ? channel : `@${channel}`;
    return `https://youtube.com/${handle}/live`;
  }
  if (platform === 'twitch') {
    if (channel.startsWith('http')) return channel;
    return `https://twitch.tv/${channel}`;
  }
  return '';
};

const PitStopRules: React.FC<{ championship: Championship }> = ({ championship }) => {
  if (!championship.mandatoryPitStop) return <p style={{ margin: 0 }}>None</p>;

  return (
    <div className="pit-stop-rules" aria-label="Mandatory pit stop">
      <span className={`pit-stop-icon ${championship.requiresTyreChange ? '' : 'is-disabled'}`} title={championship.requiresTyreChange ? 'Mandatory tyres' : 'No mandatory tyres'}>
        <RacingTyreIcon size={18} />
        {!championship.requiresTyreChange && <span className="pit-stop-icon-strike" />}
      </span>
      <span className={`pit-stop-icon ${championship.requiresRefuelling ? '' : 'is-disabled'}`} title={championship.requiresRefuelling ? 'Mandatory fuel' : 'No mandatory fuel'}>
        <Fuel size={18} />
        {!championship.requiresRefuelling && <span className="pit-stop-icon-strike" />}
      </span>
      {typeof championship.pitWindow === 'number' && (
        <span className="pit-window-badge" title="Pit stop window">
          {Math.round(championship.pitWindow / 60)}m
        </span>
      )}
    </div>
  );
};

type SortKey = 'position' | 'driverName' | 'carNumber' | 'carModel' | 'penaltyPoints' | 'totalPoints' | `round-${number}`;

const ChampionshipDetailsPage: React.FC<ChampionshipDetailsPageProps> = ({ championship, onBack, activeTab, onTabChange, broadcastPlatform = '', broadcastChannel = '', defaultRulebookType = 'text', defaultRulebook = '', defaultRulebookPdfDataUrl = null, displayTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone }) => {
  const navigate = useNavigate();
  const [rounds, setRounds] = useState<ChampionshipRound[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [standingsRows, setStandingsRows] = useState<DriverStandingsRow[]>([]);
  const [standingsLoading, setStandingsLoading] = useState(false);
  const [standingsError, setStandingsError] = useState<string | null>(null);
  const [showPointsForRounds, setShowPointsForRounds] = useState(false);
  const [showDropRounds, setShowDropRounds] = useState(false);
  const [sortConfig, setSortConfig] = useState<{ key: SortKey; direction: SortDirection }>({ key: 'totalPoints', direction: 'desc' });
  const [expandedEvents, setExpandedEvents] = useState<Set<string>>(new Set());

  const toggleEvent = (eventId: string) => {
    setExpandedEvents((prev) => {
      const next = new Set(prev);
      next.has(eventId) ? next.delete(eventId) : next.add(eventId);
      return next;
    });
  };
  const [driversSortConfig, setDriversSortConfig] = useState<{ key: 'driverName' | 'carName' | 'carNumber' | 'createdAt'; direction: SortDirection }>({
    key: 'createdAt',
    direction: 'desc',
  });
  const [viewingSession, setViewingSession] = useState<ResultSessionSummary | null>(null);
  const [replayVideoId, setReplayVideoId] = useState<string | null>(null);
  const [registration, setRegistration] = useState<ChampionshipRegistration | null>(null);
  const [registrations, setRegistrations] = useState<ChampionshipRegistration[]>(() => championship.registrations || []);
  const [registrationForm, setRegistrationForm] = useState<{ carName: string; carNumber: string; agreedRules: boolean; carSelections: RegistrationSelection[] }>({ carName: '', carNumber: '', agreedRules: false, carSelections: [] });
  const [isSubmittingRegistration, setIsSubmittingRegistration] = useState(false);
  const [registrationMessage, setRegistrationMessage] = useState<string | null>(null);
  const [registrationError, setRegistrationError] = useState<string | null>(null);
  const [showRegistrationModal, setShowRegistrationModal] = useState(false);
  const [servers, setServers] = useState<any[]>([]);
  const [visibleServerPasswords, setVisibleServerPasswords] = useState<Record<string, boolean>>({});
  const [signoutsByRaceId, setSignoutsByRaceId] = useState<Record<string, SimgridSignout[]>>({});
  const [authToken, setAuthToken] = useState<string | null>(() => {
    if (typeof window === 'undefined') return null;
    return window.localStorage.getItem('ace-public-auth-token');
  });
  const [user, setUser] = useState<{ steamId?: string; firstName?: string; lastName?: string } | null>(() => {
    if (typeof window === 'undefined') return null;
    try {
      const stored = window.localStorage.getItem('ace-public-user-profile');
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  });
  const [cars, setCars] = useState<any[]>([]);

  const eventSelections = useMemo(() => {
    return championship.events.map((event, index) => {
      const allowedCars = Array.from(new Set((event.allowed_cars_list_full || []).map((car) => car.car_name)));
      const firstRound = event.rounds?.[0];
      const label = `Round ${index + 1} - ${firstRound?.track || 'TBD'}${firstRound?.layout ? ` / ${firstRound.layout}` : ''}`;
      return {
        eventId: event.id,
        label,
        allowedCars,
      };
    });
  }, [championship.events]);

  const isPerEventSelection = Boolean(!championship.sameCarsForAllRaces);
  const registrationIsActive = Boolean(registration && registration.status !== 'left');
  const activeRegistrations = registrations.filter(
    registration => registration.status === 'active'
  );

  const isDeadlinePassed = Boolean(
    championship.registrationDeadline && new Date() > new Date(championship.registrationDeadline)
  );

  const firstRoundForCheck = championship.events?.[0]?.rounds?.[0];
  const isChampionshipStarted = Boolean(
    firstRoundForCheck && new Date() > new Date(`${firstRoundForCheck.date}T${firstRoundForCheck.startTime}`)
  );

  // Registration closed when deadline passed, OR championship started without allowLateRegistrations
  const isRegistrationClosed = isDeadlinePassed || (isChampionshipStarted && !championship.allowLateRegistrations);

  // Can still withdraw even after championship started
  const canOnlyWithdraw = isChampionshipStarted && !isDeadlinePassed;

  const getEventSelection = (eventId: string) => {
    const existing = registrationForm.carSelections.find((selection) => selection.eventId === eventId);
    if (existing?.carName) {
      return existing.carName;
    }
    const eventSelection = eventSelections.find((selection) => selection.eventId === eventId);
    if (eventSelection?.allowedCars.length === 1) {
      return eventSelection.allowedCars[0];
    }
    return '';
  };

  const handleEventSelectionChange = (eventId: string, selectedCar: string) => {
    setRegistrationForm((prev) => {
      const nextSelections = prev.carSelections.filter((selection) => selection.eventId !== eventId);
      return {
        ...prev,
        carSelections: [...nextSelections, { eventId, carName: selectedCar }],
      };
    });
  };

  useEffect(() => {
    const loadRounds = async () => {
      try {
        setLoading(true);
        setError(null);
        const res = await api.getChampionshipRounds(championship.id);
        setRounds(res.data.rounds || []);
      } catch (err) {
        console.error('Failed to load championship rounds:', err);
        setError('Unable to load championship rounds.');
      } finally {
        setLoading(false);
      }
    };

    const loadCars = async () => {
      try {
        const res = await api.getCars();
        setCars(res.data || []);
      } catch (err) {
        console.error('Failed to load cars:', err);
      }
    };

    const loadServers = async () => {
      try {
        const res = await api.getServers();
        setServers(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        console.error('Failed to load servers:', err);
      }
    };

    const loadSignouts = async () => {
      try {
        const res = await api.getChampionshipSignouts(championship.id);
        setSignoutsByRaceId(res.data.signouts || {});
      } catch (err) {
        // Signouts are optional — ignore errors
      }
    };

    loadRounds();
    loadCars();
    loadServers();
    loadSignouts();
  }, [championship.id]);

  useEffect(() => {
    if (rounds.length === 0) return;
    const now = new Date();
    const upcomingEvent = championship.events.find((event) => {
      const eventRounds = rounds.filter((r) => r.eventId === event.id);
      return eventRounds.some((r) => r.date && r.startTime && new Date(`${r.date}T${r.startTime}`) > now);
    });
    const targetId = upcomingEvent?.id ?? championship.events[championship.events.length - 1]?.id;
    if (targetId) setExpandedEvents(new Set([targetId]));
  }, [rounds, championship.events]);

  useEffect(() => {
    const loadRegistrationState = async () => {
      if (!authToken) {
        setRegistration(null);
        return;
      }
      try {
        const res = await api.getChampionshipRegistrations(championship.id);
        const nextRegistrations = res.data.registrations || [];
        setRegistrations(nextRegistrations);

        let currentSteamId: string | null = null;
        const storedProfile = window.localStorage.getItem('ace-public-user-profile');
        if (storedProfile) {
          try {
            currentSteamId = JSON.parse(storedProfile).steamId || null;
          } catch {
            currentSteamId = null;
          }
        }

        const mine = nextRegistrations.find((entry: ChampionshipRegistration) => entry.steamId === currentSteamId && entry.status !== 'left') || null;
        setRegistration(mine);
        if (mine) {
          setRegistrationForm({
            carName: mine.carName,
            carNumber: mine.carNumber,
            agreedRules: mine.agreedRules,
            carSelections: mine.carSelections || [],
          });
        }
      } catch (err) {
        console.warn('Unable to load registration state', err);
      }
    };

    loadRegistrationState();
  }, [authToken, championship.id]);

  useEffect(() => {
    if (activeTab !== 'standings') {
      return;
    }

    const loadStandings = async () => {
      try {
        setStandingsLoading(true);
        setStandingsError(null);

        const rows = new Map<string, DriverStandingsRow>();
        const raceRounds = rounds.filter((round) => round.sessions.some((session) => session.sessionType.toLowerCase() === 'race'));

        for (const round of raceRounds) {
          const raceSession = round.sessions.find((session) => session.sessionType.toLowerCase() === 'race');
          if (!raceSession) continue;

          const res = await api.getResultsSession(raceSession.sessionId.toString());
          const standings = res.data.standings || [];
          const roundIndex = raceRounds.findIndex((item) => item.roundId === round.roundId);

          for (const standing of standings) {
            const driverId = standing.unifiedDriverId ?? standing.driverId;
            const driverKey = String(driverId ?? standing.driverName);
            const existing = rows.get(driverKey);
            const points = championship.pointsSystem?.[Math.max(0, (standing.position || 1) - 1)] ?? 0;
            const nextResult = { hasResult: true, position: standing.position ?? null, points };

            if (existing) {
              existing.roundResults[roundIndex] = nextResult;
              existing.carNumber = standing.raceNumber ?? existing.carNumber;
              existing.carModel = standing.carModel ?? existing.carModel;
            } else {
              rows.set(driverKey, {
                driverId: String(driverId ?? driverKey),
                driverName: standing.driverName,
                carNumber: standing.raceNumber ?? '-',
                carModel: standing.carModel ?? 'Unknown',
                roundResults: Array.from({ length: raceRounds.length }, () => ({ hasResult: false, position: null, points: 0 })),
                droppedIndices: new Set<number>(),
                penaltyPoints: 0,
                totalPoints: 0,
              });
              rows.get(driverKey)!.roundResults[roundIndex] = nextResult;
            }
          }
        }

        const dropCount = Math.max(championship.dropRounds ?? 0, 0);

        const builtRows = Array.from(rows.values()).map((row) => {
          // Each round contributes points (0 for no result / DNS / DNF)
          const rowPointValues = row.roundResults.map((result) => (result.hasResult ? result.points : 0));
          // Find which indices to drop: the lowest N point values, stable by index
          const droppedIndices = new Set<number>();
          if (dropCount > 0) {
            const indexed = rowPointValues.map((pts, idx) => ({ pts, idx }));
            indexed.sort((a, b) => a.pts - b.pts || a.idx - b.idx);
            indexed.slice(0, dropCount).forEach(({ idx }) => droppedIndices.add(idx));
          }
          const totalPoints = rowPointValues.reduce((sum, value, idx) => sum + (droppedIndices.has(idx) ? 0 : value), 0) - row.penaltyPoints;
          return { ...row, droppedIndices, totalPoints };
        });

        builtRows.sort((a, b) => b.totalPoints - a.totalPoints || a.driverName.localeCompare(b.driverName));
        setStandingsRows(builtRows);
        setShowDropRounds(raceRounds.length > dropCount);
      } catch (err) {
        console.error('Failed to load championship standings:', err);
        setStandingsError('Unable to load championship standings.');
      } finally {
        setStandingsLoading(false);
      }
    };

    loadStandings();
  }, [activeTab, championship.dropRounds, championship.pointsSystem, rounds]);

  const selectSession = (session: ResultSessionSummary) => {
    setViewingSession(session);
  };

  const handleRegistrationSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!authToken) {
      setRegistrationError('Sign in with Discord and link your Steam account first.');
      return;
    }
    if (!registrationForm.agreedRules) {
      setRegistrationError('You must agree to the championship rules.');
      return;
    }

    if (isPerEventSelection) {
      const missing = eventSelections.some(({ eventId, allowedCars }) => allowedCars.length > 1 && !getEventSelection(eventId));
      if (missing) {
        setRegistrationError('Please select a car for every round.');
        return;
      }
    }

    try {
      setIsSubmittingRegistration(true);
      setRegistrationError(null);
      setRegistrationMessage(null);

      const payload = {
        carName: isPerEventSelection
          ? registrationForm.carSelections[0]?.carName?.trim() || registrationForm.carName.trim()
          : registrationForm.carName.trim(),
        carNumber: registrationForm.carNumber.trim(),
        agreedRules: registrationForm.agreedRules,
        ...(isPerEventSelection
          ? {
              carSelections: eventSelections
                .map(({ eventId, allowedCars }) => ({
                  eventId,
                  carName: getEventSelection(eventId) || (allowedCars.length === 1 ? allowedCars[0] : ''),
                }))
                .filter((selection) => selection.carName),
            }
          : {}),
      };

      if (registration) {
        const res = await api.updateChampionshipRegistration(championship.id, registration.id, payload, authToken);
        const nextRegistration = res.data.registration || null;
        setRegistration(nextRegistration);
        setRegistrations((prev) => {
          const filtered = prev.filter((entry) => entry.id !== nextRegistration?.id);
          return nextRegistration ? [...filtered, nextRegistration] : filtered;
        });
        setRegistrationMessage('Registration updated.');
      } else {
        const res = await api.registerForChampionship(championship.id, payload, authToken);
        const nextRegistration = res.data.registration || null;
        setRegistration(nextRegistration);
        setRegistrations((prev) => {
          if (!nextRegistration) return prev;
          const exists = prev.some((entry) => entry.id === nextRegistration.id);
          return exists
            ? prev.map((entry) => entry.id === nextRegistration.id ? nextRegistration : entry)
            : [...prev, nextRegistration];
        });
        setRegistrationMessage('Registration submitted.');
      }
    } catch (err: any) {
      console.error('Failed to submit registration', err);
      setRegistrationError(err.response?.data?.error || 'Unable to submit registration.');
    } finally {
      setIsSubmittingRegistration(false);
    }
  };

  const handleWithdrawRegistration = async () => {
    if (!authToken || !registration) return;
    try {
      setIsSubmittingRegistration(true);
      setRegistrationError(null);
      setRegistrationMessage(null);
      await api.withdrawChampionshipRegistration(championship.id, registration.id, authToken);
      setRegistration(null);
      setRegistrations((prev) => prev.map((entry) =>
        entry.id === registration.id ? { ...entry, status: 'left' as const } : entry
      ));
      setRegistrationForm({ carName: '', carNumber: '', agreedRules: false, carSelections: [] });
      setRegistrationMessage('Registration withdrawn.');
    } catch (err: any) {
      console.error('Failed to withdraw registration', err);
      setRegistrationError(err.response?.data?.error || 'Unable to withdraw registration.');
    } finally {
      setIsSubmittingRegistration(false);
    }
  };

  const raceRounds = useMemo(() => rounds.filter((round) => round.sessions.some((session) => session.sessionType.toLowerCase() === 'race')), [rounds]);
  const pointsSystemColumns = useMemo(() => {
    const schema = championship.pointsSystem || [];
    if (schema.length === 0) return [];
    return schema.map((value, index) => ({ position: index + 1, points: value }));
  }, [championship.pointsSystem]);

  const sortedRegistrations = useMemo(() => {
    return sortRows(registrations, driversSortConfig.direction, (registration) => {
      switch (driversSortConfig.key) {
        case 'driverName':
          return registration.driverName;
        case 'carName':
          return registration.carName;
        case 'carNumber':
          return registration.carNumber;
        case 'createdAt':
          return new Date(registration.createdAt).getTime();
        default:
          return '';
      }
    });
  }, [registrations, driversSortConfig]);
  const roundHeaders = useMemo(() => raceRounds.map((round, index) => ({
    key: `round-${index}` as const,
    label: `R${index + 1} · ${round.track}`,
  })), [raceRounds]);

  const isLikelyUrl = (value: string) => /^https?:\/\//i.test(value) || value.toLowerCase().endsWith('.pdf');

  const formatDurationLabel = (seconds?: number | null) => {
    if (typeof seconds !== 'number' || !Number.isFinite(seconds)) return null;
    const minutes = Math.round(seconds / 60);
    if (minutes >= 60) {
      const hours = Math.floor(minutes / 60);
      const remainder = minutes % 60;
      return remainder ? `${hours}h ${remainder}m` : `${hours}h`;
    }
    return `${minutes}m`;
  };

  const formatWeatherLabel = (weather?: string, behaviour?: string) => {
    const cleanedWeather = String(weather || '')
      .replace(/^GameModeSelectionWeatherType_/i, '')
      .replace(/_/g, ' ')
      .toLowerCase()
      .replace(/\b\w/g, (letter) => letter.toUpperCase());
    const cleanedBehaviour = String(behaviour || '')
      .replace(/^GameModeSelectionWeatherBehaviour_/i, '')
      .replace(/_/g, ' ')
      .toLowerCase()
      .replace(/\b\w/g, (letter) => letter.toUpperCase());
    return cleanedBehaviour && cleanedWeather ? `${cleanedWeather} · ${cleanedBehaviour}` : cleanedWeather || cleanedBehaviour || 'Weather TBD';
  };

  const formatRoundDateLabel = (date: string, time: string, sourceTimezone?: string) => {
    if (!date || !time) return 'Schedule TBD';
    const dt = parseRoundDateTime(date, time, sourceTimezone);
    return dt ? formatDateTime(dt, displayTimezone) : `${date} · ${time}`;
  };

  const toggleServerPasswordVisibility = (roundId: string) => {
    setVisibleServerPasswords((current) => ({ ...current, [roundId]: !current[roundId] }));
  };

  const handleSort = (key: SortKey) => {
    setSortConfig((current) => {
      if (current.key === key) {
        return { key, direction: current.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { key, direction: 'asc' };
    });
  };

  const effectiveStandingsRows = useMemo(() => {
    if (showDropRounds) return standingsRows;
    return standingsRows.map((row) => {
      const totalPoints = row.roundResults.reduce((sum, result) => sum + (result.hasResult ? result.points : 0), 0) - row.penaltyPoints;
      return { ...row, droppedIndices: new Set<number>(), totalPoints };
    });
  }, [showDropRounds, standingsRows]);

  const displayedStandingsRows = useMemo(() => {
    return sortRows(effectiveStandingsRows, sortConfig.direction, (row) => {
      const key = sortConfig.key;
      if (key === 'position') {
        return row.totalPoints;
      }
      if (key === 'driverName') {
        return row.driverName;
      }
      if (key === 'carNumber') {
        return row.carNumber;
      }
      if (key === 'carModel') {
        return row.carModel;
      }
      if (key === 'penaltyPoints') {
        return row.penaltyPoints;
      }
      if (key === 'totalPoints') {
        return row.totalPoints;
      }
      if (key.startsWith('round-')) {
        const index = Number(key.split('-')[1]);
        const result = row.roundResults[index];
        if (!result?.hasResult) {
          return showPointsForRounds ? 0 : Number.MAX_SAFE_INTEGER;
        }
        return showPointsForRounds ? result.points : (result.position ?? Number.MAX_SAFE_INTEGER);
      }
      return 0;
    });
  }, [showPointsForRounds, sortConfig, effectiveStandingsRows]);

  const renderSessionButton = (round: ChampionshipRound, type: string) => {
    if (type === 'practice' && !round.practiceLength) return null;
    if (type === 'qualify' && !round.qualifyLength) return null;

    const resultRound = rounds.find((item) => item.roundId === round.roundId || item.raceId === round.raceId);

    let session: ResultSessionSummary | undefined;
    if (type === 'practice' && resultRound) {
      // Only use practice sessions from the race server (same server as qualify or race)
      const raceServerId = resultRound.sessions.find(
        (s) => s.sessionType.toLowerCase() === 'qualify' || s.sessionType.toLowerCase() === 'race'
      )?.serverId;
      session = raceServerId
        ? resultRound.sessions.find(
            (s) => s.sessionType.toLowerCase() === 'practice' && s.serverId === raceServerId
          )
        : undefined;
    } else {
      session = resultRound?.sessions.find((s) => s.sessionType.toLowerCase() === type.toLowerCase());
    }

    const label = type === 'practice' ? 'Practice' : type === 'qualify' ? 'Quali' : 'Race';
    const duration = type === 'practice' ? round.practiceLength : type === 'qualify' ? round.qualifyLength : round.raceLength;
    const formattedDuration = formatDurationLabel(duration);
    const isAvailable = Boolean(session);

    return (
      <button
        className={`session-action-button ${isAvailable ? 'enabled' : ''}`}
        disabled={!isAvailable}
        onClick={() => session && selectSession(session)}
      >
        <span>{label}</span>
        {formattedDuration && <span className="session-action-duration">{formattedDuration}</span>}
      </button>
    );
  };

  return (
    <div className="championship-details-page">
      <div className="page-header championship-details-header">
        <div className="page-header-copy">
          <button className="small-button" onClick={onBack}>
            ← Back
          </button>
          <div className="championship-title-row">
            <h1>{championship.name}</h1>
            {registrationIsActive && <span className="status-pill live">Registered</span>}
          </div>
          <p>
            {championship.communityAbbreviation || 'Community'} · {championship.events?.length ?? 0} configured event(s)
          </p>
        </div>
        <div className="championship-header-summary">
          <div className="summary-card">
            <span className="summary-label">Season</span>
            <strong>{championship.season || 1}</strong>
          </div>
          <div className="summary-card">
            <span className="summary-label">Drop rounds</span>
            <strong>{championship.dropRounds ?? 0}</strong>
          </div>
          <div className="summary-card">
            <span className="summary-label">Drivers</span>
            <strong>{activeRegistrations.length}</strong>
          </div>
        </div>
      </div>

      <div className="registration-actions-row">
        <a
          className="small-button"
          href={api.getCalendarIcsUrl(championship.id)}
          download
          title="Download ICS calendar file for this championship"
          style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}
        >
          <Calendar size={14} /> Add to calendar
        </a>

        {championship.enableRegistrations && (
          <>
            {championship.simgridChampionshipId && championship.simgridManaged ? (
              <a
                className="small-button"
                href={`https://www.thesimgrid.com/user_championships/new?registerable_id=${championship.simgridChampionshipId}&registerable_type=Championship`}
                target="_blank"
                rel="noopener noreferrer"
              >
                Register on SimGrid
              </a>
            ) : (
              <>
                {isRegistrationClosed && !registration && (
                  <span className="muted-copy">
                    {isDeadlinePassed ? 'Registration deadline has passed' : 'Registrations are closed'}
                  </span>
                )}
                {(!isRegistrationClosed || registration) && (
                  <button
                    className="small-button"
                    onClick={() => {
                      if (!authToken || !user?.steamId || !user?.firstName || !user?.lastName) {
                        alert('Sign in with Discord, link your Steam account, and fill out your first and last name in profile settings first.');
                        return;
                      }
                      setShowRegistrationModal(true);
                    }}
                    disabled={!authToken || !user?.steamId || !user?.firstName || !user?.lastName}
                    title={!authToken || !user?.steamId || !user?.firstName || !user?.lastName ? 'Sign in with Discord, link your Steam account, and fill out your first and last name in profile settings first.' : ''}
                    style={{
                      opacity: !authToken || !user?.steamId || !user?.firstName || !user?.lastName ? 0.5 : 1,
                      cursor: !authToken || !user?.steamId || !user?.firstName || !user?.lastName ? 'not-allowed' : 'pointer',
                    }}
                  >
                    {registration ? (canOnlyWithdraw ? 'Withdraw Registration' : 'Manage Registration') : 'Register'}
                  </button>
                )}
              </>
            )}
          </>
        )}
      </div>

      <div className="section-card championship-details-shell">
        <div className="tab-switch-row">
          {['info', 'standings', 'races', 'drivers', 'practice'].map((tab) => (
            <button
              key={tab}
              className={`tab-switch ${activeTab === tab ? 'active' : ''}`}
              onClick={() => onTabChange(tab as 'info' | 'standings' | 'races' | 'drivers' | 'practice')}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>

        {activeTab === 'info' && (
          <div className="info-grid">
            {championship.broadcasted && broadcastPlatform && broadcastChannel && (() => {
              const url = buildBroadcastUrl(broadcastPlatform, broadcastChannel);
              if (!url) return null;
              return (
                <div className="info-panel">
                  <h3>Live coverage</h3>
                  <a
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="small-button"
                    style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', width: 'fit-content' }}
                    title={`Watch live on ${broadcastPlatform === 'youtube' ? 'YouTube' : 'Twitch'}`}
                  >
                    {broadcastPlatform === 'youtube' ? <Youtube size={14} /> : <Tv size={14} />}
                    Watch live on {broadcastPlatform === 'youtube' ? 'YouTube' : 'Twitch'}
                  </a>
                </div>
              );
            })()}

            <div className="info-panel">
              {/* <h3>At a glance</h3> */}
              <div className="stats-grid">
                <div className="stat-tile">
                  <span className="stat-label">Season</span>
                  <span className="stat-value">{championship.season || 1}</span>
                </div>
                <div className="stat-tile">
                  <span className="stat-label">Events</span>
                  <span className="stat-value">{championship.events?.length ?? 0}</span>
                </div>
                <div className="stat-tile">
                  <span className="stat-label">Drop rounds</span>
                  <span className="stat-value">{championship.dropRounds ?? 0}</span>
                </div>
              </div>
            </div>

            <div className="info-panel">
              <h3>Description</h3>
              <p style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{championship.description || 'No description provided yet.'}</p>
            </div>

            <div className="info-panel">
              <h3>Points system</h3>
              {pointsSystemColumns.length > 0 ? (
                <div className="table-shell">
                  <table className="standings-table" style={{ minWidth: '280px' }}>
                    <thead>
                      <tr>
                        <th>Position</th>
                        <th>Points</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pointsSystemColumns.map((item) => (
                        <tr key={item.position}>
                          <td>{item.position}</td>
                          <td>{item.points}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p style={{ margin: 0 }}>No points system configured yet.</p>
              )}
            </div>

            <div className="info-panel">
              <h3>Pit stop rules</h3>
              <PitStopRules championship={championship} />
            </div>

            <div className="info-panel">
              <h3>Rules</h3>
              {(() => {
                const effectiveRulebookType = championship.rulebookType || (defaultRulebook ? defaultRulebookType : undefined);
                const effectiveRulebookValue = championship.rulebook?.trim() || defaultRulebook?.trim() || '';
                const effectiveRulebookPdf = championship.rulebookPdfDataUrl || defaultRulebookPdfDataUrl;

                if (effectiveRulebookType === 'link' && effectiveRulebookValue) {
                  return <a href={effectiveRulebookValue} target="_blank" rel="noreferrer" className="small-button" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', width: 'fit-content' }}>Open rules document</a>;
                }

                if (effectiveRulebookType === 'pdf' && effectiveRulebookPdf) {
                  return (
                    <button type="button" className="small-button" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', width: 'fit-content' }}
                      onClick={() => { const w = window.open(); if (w) { w.document.write(`<iframe src="${effectiveRulebookPdf}" style="width:100%;height:100%;border:none"></iframe>`); } }}>
                      View rulebook PDF
                    </button>
                  );
                }

                if (effectiveRulebookType === 'text' && effectiveRulebookValue) {
                  return <p style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{effectiveRulebookValue}</p>;
                }

                return <p style={{ margin: 0 }}>No rules provided yet.</p>;
              })()}
            </div>
          </div>
        )}

        {activeTab === 'standings' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', gap: '12px', flexWrap: 'wrap' }}>
              <div>
                <strong>View</strong>
                <div style={{ fontSize: '0.8rem', color: 'var(--muted-text, #9ca3af)' }}>Drop rounds: {championship.dropRounds ?? 0}</div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                <button
                  type="button"
                  onClick={() => setShowPointsForRounds((current) => !current)}
                  aria-pressed={showPointsForRounds}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    borderRadius: '999px',
                    border: '1px solid var(--border-color)',
                    padding: '4px',
                    background: 'var(--card-bg, #111827)',
                    color: 'inherit',
                    cursor: 'pointer',
                  }}
                >
                  <span style={{ padding: '6px 10px', borderRadius: '999px', background: !showPointsForRounds ? 'var(--secondary-color, #4f46e5)' : 'transparent', color: !showPointsForRounds ? '#fff' : 'inherit' }}>Positions</span>
                  <span style={{ padding: '6px 10px', borderRadius: '999px', background: showPointsForRounds ? 'var(--secondary-color, #4f46e5)' : 'transparent', color: showPointsForRounds ? '#fff' : 'inherit' }}>Points</span>
                </button>
                {(championship.dropRounds ?? 0) > 0 && (
                  <button
                    type="button"
                    onClick={() => setShowDropRounds((current) => !current)}
                    aria-pressed={showDropRounds}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '8px',
                      borderRadius: '999px',
                      border: '1px solid var(--border-color)',
                      padding: '4px',
                      background: 'var(--card-bg, #111827)',
                      color: 'inherit',
                      cursor: 'pointer',
                    }}
                  >
                    <span style={{ padding: '6px 10px', borderRadius: '999px', background: !showDropRounds ? 'var(--secondary-color, #4f46e5)' : 'transparent', color: !showDropRounds ? '#fff' : 'inherit' }}>All rounds</span>
                    <span style={{ padding: '6px 10px', borderRadius: '999px', background: showDropRounds ? 'var(--secondary-color, #4f46e5)' : 'transparent', color: showDropRounds ? '#fff' : 'inherit' }}>Drop rounds</span>
                  </button>
                )}
              </div>
            </div>

            {standingsLoading && <div className="empty-state">Loading standings...</div>}
            {standingsError && <div className="empty-state">{standingsError}</div>}
            {!standingsLoading && !standingsError && (
              <div className="table-shell">
                <table className="standings-table" style={{ minWidth: '760px' }}>
                  <thead>
                    <tr>
                      <th onClick={() => handleSort('position')} style={{ cursor: 'pointer' }}><span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}># <span aria-hidden="true">{getSortIndicator(sortConfig.key, 'position', sortConfig.direction)}</span></span></th>
                      <th onClick={() => handleSort('driverName')} style={{ cursor: 'pointer' }}><span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>Driver <span aria-hidden="true">{getSortIndicator(sortConfig.key, 'driverName', sortConfig.direction)}</span></span></th>
                      <th onClick={() => handleSort('carNumber')} style={{ cursor: 'pointer' }}><span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>Car # <span aria-hidden="true">{getSortIndicator(sortConfig.key, 'carNumber', sortConfig.direction)}</span></span></th>
                      <th onClick={() => handleSort('carModel')} style={{ cursor: 'pointer' }}><span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>Car <span aria-hidden="true">{getSortIndicator(sortConfig.key, 'carModel', sortConfig.direction)}</span></span></th>
                      {roundHeaders.map((header) => (
                        <th key={header.key} onClick={() => handleSort(header.key as SortKey)} style={{ cursor: 'pointer' }}>
                          <div className="vertical-header">{header.label}</div>
                        </th>
                      ))}
                      <th onClick={() => handleSort('penaltyPoints')} style={{ cursor: 'pointer' }}><span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>Penalty <span aria-hidden="true">{getSortIndicator(sortConfig.key, 'penaltyPoints', sortConfig.direction)}</span></span></th>
                      <th onClick={() => handleSort('totalPoints')} style={{ cursor: 'pointer' }}><span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>Total <span aria-hidden="true">{getSortIndicator(sortConfig.key, 'totalPoints', sortConfig.direction)}</span></span></th>
                    </tr>
                  </thead>
                  <tbody>
                    {standingsRows.length === 0 ? (
                      <tr>
                        <td colSpan={8 + roundHeaders.length} className="empty-state">No race results available for this championship yet.</td>
                      </tr>
                    ) : (
                      displayedStandingsRows.map((row, index) => (
                        <tr key={row.driverId}>
                          <td>{index + 1}</td>
                          <td>{row.driverName}</td>
                          <td>{row.carNumber}</td>
                          <td>{row.carModel}</td>
                          {row.roundResults.map((result, resultIndex) => {
                            const isDropped = row.droppedIndices.has(resultIndex);
                            const droppedStyle: React.CSSProperties = isDropped ? { textDecoration: 'line-through', opacity: 0.5 } : {};
                            return (
                              <td key={`${row.driverId}-${resultIndex}`} style={{ textAlign: 'center' }}>
                                {result.hasResult ? (
                                  showPointsForRounds ? (
                                    <span style={droppedStyle}>{result.points}</span>
                                  ) : (
                                    <span style={{ ...droppedStyle, color: result.position === null ? 'var(--muted-text, #9ca3af)' : 'inherit' }}>
                                      {result.position ?? '—'}
                                    </span>
                                  )
                                ) : (
                                  <span style={{ ...droppedStyle, color: 'var(--muted-text, #9ca3af)', fontStyle: 'italic' }}>—</span>
                                )}
                              </td>
                            );
                          })}
                          <td style={{ textAlign: 'center' }}>{row.penaltyPoints}</td>
                          <td style={{ textAlign: 'center' }}>{row.totalPoints}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {activeTab === 'drivers' && (
          <div>
            {/* <div className="card" style={{ marginBottom: '12px' }}>
              <h3 style={{ marginTop: 0 }}>Registration</h3>
              {!authToken ? (
                <p>Sign in and link your Steam account to register for this championship.</p>
              ) : (
                <form onSubmit={handleRegistrationSubmit} style={{ display: 'grid', gap: '12px' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px' }}>
                    <label style={{ display: 'grid', gap: '4px' }}>
                      <span>Car</span>
                      <input value={registrationForm.carName} onChange={(e) => setRegistrationForm((prev) => ({ ...prev, carName: e.target.value }))} placeholder="BMW M4" required />
                    </label>
                    <label style={{ display: 'grid', gap: '4px' }}>
                      <span>Car Number</span>
                      <input value={registrationForm.carNumber} onChange={(e) => setRegistrationForm((prev) => ({ ...prev, carNumber: e.target.value }))} placeholder="7" required />
                    </label>
                  </div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input type="checkbox" checked={registrationForm.agreedRules} onChange={(e) => setRegistrationForm((prev) => ({ ...prev, agreedRules: e.target.checked }))} />
                    <span>I agree to the championship rules.</span>
                  </label>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    <button type="submit" className="small-button" disabled={isSubmittingRegistration}>
                      {registration ? 'Update Registration' : 'Register'}
                    </button>
                    {registration && (
                      <button type="button" className="small-button" onClick={handleWithdrawRegistration} disabled={isSubmittingRegistration}>
                        Withdraw
                      </button>
                    )}
                  </div>
                  {registrationMessage && <p style={{ margin: 0, color: 'var(--success-color, #34d399)' }}>{registrationMessage}</p>}
                  {registrationError && <p style={{ margin: 0, color: 'var(--error-color, #f87171)' }}>{registrationError}</p>}
                </form>
              )}
            </div> */}

            <div className="info-panel">
              <h3>Registered Drivers ({registrations.filter((r) => r.status !== 'left').length})</h3>
              {registrations.length === 0 ? (
                <p>No registrations yet.</p>
              ) : (
                <div className="table-shell">
                  <table className="drivers-table" style={{ minWidth: '600px' }}>
                    <thead>
                      <tr>
                        <th
                          onClick={() => {
                            const newDirection = driversSortConfig.key === 'driverName' && driversSortConfig.direction === 'asc' ? 'desc' : 'asc';
                            setDriversSortConfig({ key: 'driverName', direction: newDirection });
                          }}
                          style={{ cursor: 'pointer' }}
                        >
                          Driver {driversSortConfig.key === 'driverName' && (driversSortConfig.direction === 'asc' ? '↑' : '↓')}
                        </th>
                        <th
                          onClick={() => {
                            const newDirection = driversSortConfig.key === 'carName' && driversSortConfig.direction === 'asc' ? 'desc' : 'asc';
                            setDriversSortConfig({ key: 'carName', direction: newDirection });
                          }}
                          style={{ cursor: 'pointer' }}
                        >
                          Car {driversSortConfig.key === 'carName' && (driversSortConfig.direction === 'asc' ? '↑' : '↓')}
                        </th>
                        <th
                          onClick={() => {
                            const newDirection = driversSortConfig.key === 'carNumber' && driversSortConfig.direction === 'asc' ? 'desc' : 'asc';
                            setDriversSortConfig({ key: 'carNumber', direction: newDirection });
                          }}
                          style={{ cursor: 'pointer', textAlign: 'center' }}
                        >
                          # {driversSortConfig.key === 'carNumber' && (driversSortConfig.direction === 'asc' ? '↑' : '↓')}
                        </th>
                        <th
                          onClick={() => {
                            const newDirection = driversSortConfig.key === 'createdAt' && driversSortConfig.direction === 'asc' ? 'desc' : 'asc';
                            setDriversSortConfig({ key: 'createdAt', direction: newDirection });
                          }}
                          style={{ cursor: 'pointer' }}
                        >
                          Registered {driversSortConfig.key === 'createdAt' && (driversSortConfig.direction === 'asc' ? '↑' : '↓')}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedRegistrations.map((entry) => {
                        const carData = cars.find((c) => c.name === entry.carName);
                        const carDisplayName = carData?.display_name || entry.carName;
                        const hasLeft = entry.status === 'left';
                        return (
                          <tr key={entry.id} style={{ opacity: hasLeft ? 0.55 : 1 }}>
                            <td>
                              <a
                                href={`/drivers/${entry.steamId}`}
                                style={{ color: 'var(--secondary-color, #4f46e5)', textDecoration: 'none', fontWeight: 500, cursor: 'pointer' }}
                                onClick={async (e) => {
                                  e.preventDefault();
                                  try {
                                    const res = await api.getDriverBySteamId(entry.steamId);
                                    if (res.data.driver?.id) {
                                      navigate(`/drivers/${res.data.driver.id}`);
                                    } else {
                                      navigate(`/drivers/${entry.steamId}`);
                                    }
                                  } catch (err) {
                                    console.error('Failed to lookup driver:', err);
                                    navigate(`/drivers/${entry.steamId}`);
                                  }
                                }}
                              >
                                {entry.driverName}
                              </a>
                              {hasLeft && (
                                <span style={{ marginLeft: '8px', fontSize: '0.75rem', color: 'var(--muted)', background: 'var(--surface-strong)', padding: '1px 6px', borderRadius: '4px' }}>
                                  Left
                                </span>
                              )}
                            </td>
                            <td>{carDisplayName}</td>
                            <td style={{ textAlign: 'center', fontWeight: 600 }}>#{entry.carNumber}</td>
                            <td style={{ color: 'var(--muted)', fontSize: '0.9rem' }}>
                              {new Date(entry.createdAt).toLocaleDateString()} {new Date(entry.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'races' && (
          <div>
            {loading && <div className="empty-state">Loading rounds...</div>}
            {error && <div className="empty-state">{error}</div>}
            {!loading && !error && (
              <div className="info-grid">
                {(championship.events || []).map((event, eventIndex) => {
                  const eventRounds = rounds.filter((round) => round.eventId === event.id);
                  const isExpanded = expandedEvents.has(event.id);
                  const roundCount = eventRounds.length;
                  return (
                    <div key={event.id} className="round-card">
                      <button
                        type="button"
                        className="round-card-header round-card-toggle"
                        onClick={() => toggleEvent(event.id)}
                        aria-expanded={isExpanded}
                      >
                        <div className="round-card-title">
                          <h3>Event {event.eventNumber || eventIndex + 1}</h3>
                          {!isExpanded && eventRounds[0] && (
                            <p className="event-collapsed-meta">
                              {eventRounds[0].track}{eventRounds[0].layout ? ` / ${eventRounds[0].layout}` : ''} · {formatRoundDateLabel(eventRounds[0].date, eventRounds[0].startTime, eventRounds[0].timezone)}
                            </p>
                          )}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          {!isExpanded && eventRounds[0] && (
                            <>
                              <span className="meta-pill event-collapsed-pill"><CloudSun size={14} /> {formatWeatherLabel(eventRounds[0].weather, eventRounds[0].weatherBehaviour)}</span>
                              <span className="meta-pill event-collapsed-pill"><CalendarClock size={14} /> In-game {eventRounds[0].ingameTime || 'TBD'}</span>
                            </>
                          )}
                          <span className="meta-pill">{roundCount} {roundCount === 1 ? 'round' : 'rounds'}</span>
                          <ChevronDown size={16} className={`chevron-icon ${isExpanded ? 'rotated' : ''}`} />
                        </div>
                      </button>

                      {isExpanded && (
                        <div className="round-card-body">
                          {eventRounds.length === 0 ? (
                            <div className="empty-state">No rounds found for this event yet.</div>
                          ) : (
                            eventRounds.map((round) => {
                              const serverForRound = servers.find((server) => server.raceId === round.raceId);
                              const serverPassword = serverForRound?.serverConfig?.driver_password || championship.driverPassword || '';
                              const passwordHidden = !visibleServerPasswords[round.roundId];
                              const abbr = championship.communityAbbreviation || '';
                              const season = String(championship.season ?? 1).padStart(2, '0');
                              const eventNr = String(event.eventNumber || eventIndex + 1).padStart(2, '0');
                              const track = round.track || 'TBD';
                              const championshipName = championship.name || '';
                              const serverBaseName = abbr ? `${abbr} - S${season}R${eventNr} - ${track} - ${championshipName}` : `S${season}R${eventNr} - ${track} - ${championshipName}`;
                              return (
                                <div key={round.roundId} className="info-panel">
                                  <div className="round-card-header">
                                    <div className="round-card-title">
                                      <h3>{round.track} / {round.layout}</h3>
                                      <p>{formatRoundDateLabel(round.date, round.startTime, round.timezone)}</p>
                                    </div>
                                    <div className="round-card-meta">
                                      <span className="meta-pill"><CloudSun size={14} /> {formatWeatherLabel(round.weather, round.weatherBehaviour)}</span>
                                      <span className="meta-pill"><CalendarClock size={14} /> In-game {round.ingameTime || 'TBD'}</span>
                                    </div>
                                  </div>

                                  {authToken && registrationIsActive ? (
                                    <div className="access-details">
                                      <span className="access-details-header">Access details</span>
                                      <span>Practice Server: <strong>{serverBaseName} - Practice</strong></span>
                                      <span>Race Server: <strong>{serverBaseName} - Race</strong></span>
                                      <span>
                                        Race Server Password:{' '}
                                        <strong className="access-password-value">
                                          {serverPassword ? (
                                            passwordHidden ? '••••••••' : serverPassword
                                          ) : (
                                            <span style={{ color: 'var(--muted)', fontWeight: 400 }}>No password</span>
                                          )}
                                        </strong>
                                        {serverPassword && (
                                          <button
                                            type="button"
                                            className="icon-button access-password-toggle"
                                            onClick={() => toggleServerPasswordVisibility(round.roundId)}
                                            aria-label={passwordHidden ? 'Show password' : 'Hide password'}
                                          >
                                            {passwordHidden ? <Eye size={13} /> : <EyeOff size={13} />}
                                          </button>
                                        )}
                                      </span>
                                    </div>
                                  ) : authToken ? (
                                    <p className="access-details-locked">Register to view server and password.</p>
                                  ) : null}

                                  <div className="session-actions-group">
                                    <span className="session-actions-header">Results</span>
                                    <div className="session-actions">
                                      {renderSessionButton(round, 'practice')}
                                      {renderSessionButton(round, 'qualify')}
                                      {renderSessionButton(round, 'race')}
                                      {round.vodVideoId && (
                                        <button
                                          type="button"
                                          className="session-action-button enabled"
                                          onClick={() => setReplayVideoId(round.vodVideoId!)}
                                        >
                                          <Youtube size={14} />
                                          <span>Replay</span>
                                        </button>
                                      )}
                                    </div>
                                  </div>

                                  {(() => {
                                    const roundSignouts: SimgridSignout[] = signoutsByRaceId[round.raceId] || [];
                                    const mySignout = user?.steamId
                                      ? roundSignouts.find((s) => String(s.signoutable_id) === user.steamId)
                                      : null;
                                    const canSelfManage = registrationIsActive && authToken && user?.steamId;
                                    return (
                                      <div className="session-actions-group" style={{ marginTop: '8px' }}>
                                        <span className="session-actions-header">Sign-outs</span>
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '4px', alignItems: 'center' }}>
                                          {roundSignouts.length === 0 && !canSelfManage && (
                                            <span style={{ fontSize: '0.8rem', color: 'var(--muted-text, #9ca3af)' }}>None</span>
                                          )}
                                          {roundSignouts.map((signout) => (
                                            <span
                                              key={signout.id}
                                              style={{
                                                fontSize: '0.8rem',
                                                padding: '3px 8px',
                                                borderRadius: '4px',
                                                background: 'var(--surface-strong, rgba(255,255,255,0.07))',
                                                color: 'var(--muted-text, #9ca3af)',
                                                border: '1px solid var(--border-color)',
                                              }}
                                            >
                                              {signout.signoutable}
                                            </span>
                                          ))}
                                          {championship.simgridChampionshipId && championship.simgridManaged ? (
                                            <a
                                              className="small-button"
                                              href={`https://www.thesimgrid.com/championships/${championship.simgridChampionshipId}/races`}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              style={{ fontSize: '0.8rem', marginLeft: roundSignouts.length ? '4px' : '0' }}
                                            >
                                              Manage on SimGrid
                                            </a>
                                          ) : canSelfManage && (
                                            mySignout ? (
                                              <button
                                                className="small-button"
                                                style={{ fontSize: '0.8rem' }}
                                                onClick={async () => {
                                                  try {
                                                    await api.selfSignBackIn(championship.id, round.eventId!, round.roundId, authToken!);
                                                    const res = await api.getChampionshipSignouts(championship.id);
                                                    setSignoutsByRaceId(res.data.signouts || {});
                                                  } catch (err: any) {
                                                    alert(err?.response?.data?.error || 'Failed to sign back in');
                                                  }
                                                }}
                                              >
                                                Sign back in
                                              </button>
                                            ) : (
                                              <button
                                                className="small-button"
                                                style={{ fontSize: '0.8rem' }}
                                                onClick={async () => {
                                                  try {
                                                    await api.selfSignOut(championship.id, round.eventId!, round.roundId, authToken!);
                                                    const res = await api.getChampionshipSignouts(championship.id);
                                                    setSignoutsByRaceId(res.data.signouts || {});
                                                  } catch (err: any) {
                                                    alert(err?.response?.data?.error || 'Failed to sign out');
                                                  }
                                                }}
                                              >
                                                Sign out
                                              </button>
                                            )
                                          )}
                                        </div>
                                      </div>
                                    );
                                  })()}
                                </div>
                              );
                            })
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}

                {championship.events.length === 0 && (
                  <div className="empty-state">No events configured for this championship.</div>
                )}
              </div>
            )}
          </div>
        )}

        {activeTab === 'practice' && (
          <PracticeTab championship={championship} servers={servers} rounds={rounds} />
        )}

        {replayVideoId && (
          <div
            style={{ position: 'fixed', inset: 0, background: 'rgba(2,6,23,0.82)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000, padding: '16px' }}
            onClick={() => setReplayVideoId(null)}
          >
            <div
              style={{ width: 'min(96vw, 1100px)', background: 'linear-gradient(180deg,rgba(9,18,35,0.99),rgba(6,12,24,0.99))', border: '1px solid rgba(148,163,184,0.2)', borderRadius: '16px', padding: '16px', boxShadow: '0 24px 60px rgba(0,0,0,0.4)' }}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <span style={{ fontWeight: 700, color: '#f8fafc', display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                  <Youtube size={16} /> Race replay
                </span>
                <button
                  type="button"
                  className="icon-button"
                  onClick={() => setReplayVideoId(null)}
                  style={{ lineHeight: 1 }}
                >
                  <X size={16} />
                </button>
              </div>
              <div style={{ position: 'relative', aspectRatio: '16/9', borderRadius: '10px', overflow: 'hidden', background: '#000' }}>
                <iframe
                  src={`https://www.youtube.com/embed/${replayVideoId}?autoplay=1`}
                  title="Race replay"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 'none' }}
                />
              </div>
            </div>
          </div>
        )}
      </div>

      <ChampionshipRegistrationModal
        open={showRegistrationModal}
        title={registration ? (canOnlyWithdraw ? 'Withdraw Registration' : 'Manage Registration') : 'Register for Championship'}
        subtitle={championship.name}
        onClose={() => setShowRegistrationModal(false)}
      >
        {canOnlyWithdraw && registration ? (
          <div style={{ display: 'grid', gap: '16px' }}>
            <p style={{ margin: 0, color: 'var(--muted-text, #9ca3af)', fontSize: '0.9rem' }}>
              The championship has started. You can no longer change your car or race number, but you may withdraw your registration.
            </p>
            <div style={{ padding: '12px', background: 'var(--card-bg, #111827)', border: '1px solid var(--border-color)', borderRadius: '8px' }}>
              <div style={{ fontSize: '0.85rem', color: 'var(--muted-text, #9ca3af)', marginBottom: '4px' }}>Current registration</div>
              <div style={{ fontWeight: 600 }}>{registration.driverName}</div>
              <div style={{ fontSize: '0.9rem', color: 'var(--muted-text, #9ca3af)' }}>#{registration.carNumber} · {registration.carName}</div>
            </div>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              <button type="button" className="small-button" onClick={handleWithdrawRegistration} disabled={isSubmittingRegistration}>
                Withdraw
              </button>
            </div>
            {registrationMessage && <p style={{ margin: 0, color: 'var(--success-color, #34d399)' }}>{registrationMessage}</p>}
            {registrationError && <p style={{ margin: 0, color: 'var(--error-color, #f87171)' }}>{registrationError}</p>}
          </div>
        ) : (
        <form onSubmit={handleRegistrationSubmit} style={{ display: 'grid', gap: '12px' }}>
          {isPerEventSelection ? (
            <div className="championship-registration-table">
              <div className="table-row table-header">
                <div className="table-cell">Round</div>
                <div className="table-cell">Track / Layout</div>
                <div className="table-cell">Car Selection</div>
              </div>
              {eventSelections.map(({ eventId, label, allowedCars }) => {
                const selectedCar = getEventSelection(eventId);
                const [roundNumber, trackLayout] = label.split(' - ', 2);
                return (
                  <div className="table-row" key={eventId}>
                    <div className="table-cell">{roundNumber}</div>
                    <div className="table-cell">{trackLayout}</div>
                    <div className="table-cell">
                      <select
                        value={selectedCar}
                        onChange={(event) => handleEventSelectionChange(eventId, event.target.value)}
                        required
                        disabled={allowedCars.length === 1}
                      >
                        {allowedCars.length === 0 ? (
                          <option value="">No cars available</option>
                        ) : (
                          <>
                            <option value="">Select a car</option>
                            {allowedCars.map((carName) => {
                              const carData = cars.find((c) => c.name === carName);
                              const displayName = carData?.display_name || carName;
                              return (
                                <option key={carName} value={carName}>
                                  {displayName}
                                </option>
                              );
                            })}
                          </>
                        )}
                      </select>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div style={{ display: 'grid', gap: '12px' }}>
              <label style={{ display: 'grid', gap: '4px' }}>
                <span>Car</span>
                <select value={registrationForm.carName} onChange={(e) => setRegistrationForm((prev) => ({ ...prev, carName: e.target.value }))} required>
                  <option value="">Select a car</option>
                  {Array.from(new Set(championship.events.flatMap((event) => event.allowed_cars_list_full || []).map((car) => car.car_name))).map((carName) => {
                    const carData = cars.find((c) => c.name === carName);
                    const displayName = carData?.display_name || carName;
                    return (
                      <option key={carName} value={carName}>
                        {displayName}
                      </option>
                    );
                  })}
                </select>
              </label>
            </div>
          )}
          <div style={{ display: 'grid', gap: '12px' }}>
            <label style={{ display: 'grid', gap: '4px' }}>
              <span>Car Number</span>
              <input
                value={registrationForm.carNumber}
                onChange={(e) => {
                  const value = e.target.value;
                  // Check if car number is already taken by another registration
                  const isDuplicate = registrations.some(
                    (reg) => reg.carNumber === value && reg.steamId !== user?.steamId && reg.status !== 'left'
                  );
                  if (isDuplicate) {
                    setRegistrationError(`Car number ${value} is already taken.`);
                  } else if (registrationError && registrationError.includes('already taken')) {
                    setRegistrationError(null);
                  }
                  setRegistrationForm((prev) => ({ ...prev, carNumber: value }));
                }}
                placeholder="7"
                required
              />
              {registrationError && registrationError.includes('already taken') && (
                <span style={{ color: 'var(--error-color, #f87171)', fontSize: '0.85rem', marginTop: '4px' }}>
                  {registrationError}
                </span>
              )}
            </label>
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <input type="checkbox" checked={registrationForm.agreedRules} onChange={(e) => setRegistrationForm((prev) => ({ ...prev, agreedRules: e.target.checked }))} />
            <span>I agree to the championship rules.</span>
          </label>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <button type="submit" className="small-button" disabled={isSubmittingRegistration}>
              {registration ? 'Update Registration' : 'Register'}
            </button>
            {registration && (
              <button type="button" className="small-button" onClick={handleWithdrawRegistration} disabled={isSubmittingRegistration}>
                Withdraw
              </button>
            )}
          </div>
          {registrationMessage && <p style={{ margin: 0, color: 'var(--success-color, #34d399)' }}>{registrationMessage}</p>}
          {registrationError && <p style={{ margin: 0, color: 'var(--error-color, #f87171)' }}>{registrationError}</p>}
        </form>
        )}
      </ChampionshipRegistrationModal>

      {viewingSession && (
        <RaceResultsViewer
          sessionId={viewingSession.sessionId}
          onClose={() => setViewingSession(null)}
          fetchResult={async () => api.getResultsSession(viewingSession.sessionId.toString())}
        />
      )}
    </div>
  );
};

export default ChampionshipDetailsPage;
